import React, { useState, useEffect } from 'react'
import { VendorProduct, ProductSize, Vendor } from '../types'
import { mockVendors } from '../data/mockVendors'

interface StaffProductManagerProps {
  onProductAdded?: (product: VendorProduct) => void
}

export default function StaffProductManager({ onProductAdded }: StaffProductManagerProps) {
  const [vendors, setVendors] = useState<Vendor[]>(mockVendors)
  const [selectedVendor, setSelectedVendor] = useState<string>('')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    extractType: '',
    type: 'powder' as const,
    categories: [] as string[],
    tags: [] as string[],
    featured: false,
    inStock: true,
    potency: '',
    images: [] as string[]
  })
  const [sizes, setSizes] = useState<ProductSize[]>([{ weight: '', price: 0, inStock: true }])
  const [labResults, setLabResults] = useState({
    hasResults: false,
    '7oh': 0,
    mitragynine: 0,
    testDate: '',
    labName: ''
  })
  const [newCategory, setNewCategory] = useState('')
  const [newTag, setNewTag] = useState('')
  const [imageUrl, setImageUrl] = useState('')

  const productTypes = ['powder', 'capsules', 'extract', 'tablet', 'other']
  const commonCategories = ['7OH Products', 'MIT Extracts', 'Plain Leaf', 'Capsules', 'Tablets', 'Bulk Products']
  const commonTags = ['premium', 'lab-tested', 'organic', 'small-batch', 'bestseller', 'new-arrival']

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addSize = () => {
    setSizes(prev => [...prev, { weight: '', price: 0, inStock: true }])
  }

  const updateSize = (index: number, field: keyof ProductSize, value: any) => {
    setSizes(prev => prev.map((size, i) => 
      i === index ? { ...size, [field]: value } : size
    ))
  }

  const removeSize = (index: number) => {
    setSizes(prev => prev.filter((_, i) => i !== index))
  }

  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      handleInputChange('categories', [...formData.categories, newCategory.trim()])
      setNewCategory('')
    }
  }

  const removeCategory = (category: string) => {
    handleInputChange('categories', formData.categories.filter(c => c !== category))
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      handleInputChange('tags', [...formData.tags, newTag.trim()])
      setNewTag('')
    }
  }

  const removeTag = (tag: string) => {
    handleInputChange('tags', formData.tags.filter(t => t !== tag))
  }

  const addImage = () => {
    if (imageUrl.trim() && !formData.images.includes(imageUrl.trim())) {
      handleInputChange('images', [...formData.images, imageUrl.trim()])
      setImageUrl('')
    }
  }

  const removeImage = (url: string) => {
    handleInputChange('images', formData.images.filter(img => img !== url))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedVendor || !formData.name || !formData.description || sizes.length === 0) {
      alert('Please fill in all required fields')
      return
    }

    const newProduct: VendorProduct = {
      id: `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: formData.name,
      description: formData.description,
      extractType: formData.extractType,
      type: formData.type,
      sizes: sizes.filter(size => size.weight && size.price > 0),
      images: formData.images,
      inStock: formData.inStock,
      featured: formData.featured,
      categories: formData.categories,
      tags: formData.tags,
      dateAdded: new Date(),
      salesCount: 0,
      rating: 0,
      reviewCount: 0,
      potency: formData.potency || undefined,
      labResults: labResults.hasResults ? {
        '7oh': labResults['7oh'],
        mitragynine: labResults.mitragynine,
        testDate: new Date(labResults.testDate),
        labName: labResults.labName
      } : undefined
    }

    onProductAdded?.(newProduct)
    
    // Reset form
    setFormData({
      name: '',
      description: '',
      extractType: '',
      type: 'powder',
      categories: [],
      tags: [],
      featured: false,
      inStock: true,
      potency: '',
      images: []
    })
    setSizes([{ weight: '', price: 0, inStock: true }])
    setLabResults({
      hasResults: false,
      '7oh': 0,
      mitragynine: 0,
      testDate: '',
      labName: ''
    })
    setSelectedVendor('')
    
    alert('Product added successfully!')
  }

  return (
    <div className="staff-product-manager">
      <div className="product-manager-header">
        <h2>Staff Product Management</h2>
        <p>Add and manage products for vendors</p>
      </div>

      <form onSubmit={handleSubmit} className="product-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="vendor-select">Select Vendor *</label>
            <select
              id="vendor-select"
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              required
            >
              <option value="">Choose a vendor...</option>
              {vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="product-name">Product Name *</label>
            <input
              id="product-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Premium 7OH Tablets"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="product-description">Description *</label>
            <textarea
              id="product-description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Detailed product description..."
              rows={4}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="product-type">Product Type *</label>
              <select
                id="product-type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                required
              >
                {productTypes.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="extract-type">Extract Type</label>
              <input
                id="extract-type"
                type="text"
                value={formData.extractType}
                onChange={(e) => handleInputChange('extractType', e.target.value)}
                placeholder="e.g., 7OH, MIT, Plain Leaf"
              />
            </div>

            <div className="form-group">
              <label htmlFor="potency">Potency</label>
              <input
                id="potency"
                type="text"
                value={formData.potency}
                onChange={(e) => handleInputChange('potency', e.target.value)}
                placeholder="e.g., 10mg per tab, 5% 7OH"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.inStock}
                  onChange={(e) => handleInputChange('inStock', e.target.checked)}
                />
                In Stock
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleInputChange('featured', e.target.checked)}
                />
                Featured Product
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Sizes & Pricing</h3>
          {sizes.map((size, index) => (
            <div key={index} className="size-row">
              <div className="form-group">
                <label>Weight/Size</label>
                <input
                  type="text"
                  value={size.weight}
                  onChange={(e) => updateSize(index, 'weight', e.target.value)}
                  placeholder="e.g., 10g, 100 caps, 1oz"
                />
              </div>
              <div className="form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={size.price}
                  onChange={(e) => updateSize(index, 'price', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={size.inStock}
                    onChange={(e) => updateSize(index, 'inStock', e.target.checked)}
                  />
                  In Stock
                </label>
              </div>
              {sizes.length > 1 && (
                <button type="button" onClick={() => removeSize(index)} className="remove-btn">
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addSize} className="add-btn">
            Add Size
          </button>
        </div>

        <div className="form-section">
          <h3>Categories</h3>
          <div className="tag-input-section">
            <div className="common-options">
              <label>Common Categories:</label>
              <div className="button-group">
                {commonCategories.map(category => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => !formData.categories.includes(category) && 
                      handleInputChange('categories', [...formData.categories, category])}
                    className={`tag-btn ${formData.categories.includes(category) ? 'selected' : ''}`}
                    disabled={formData.categories.includes(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
            <div className="custom-input">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Add custom category..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
              />
              <button type="button" onClick={addCategory}>Add</button>
            </div>
          </div>
          <div className="selected-tags">
            {formData.categories.map(category => (
              <span key={category} className="tag">
                {category}
                <button type="button" onClick={() => removeCategory(category)}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="form-section">
          <h3>Tags</h3>
          <div className="tag-input-section">
            <div className="common-options">
              <label>Common Tags:</label>
              <div className="button-group">
                {commonTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => !formData.tags.includes(tag) && 
                      handleInputChange('tags', [...formData.tags, tag])}
                    className={`tag-btn ${formData.tags.includes(tag) ? 'selected' : ''}`}
                    disabled={formData.tags.includes(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="custom-input">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add custom tag..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <button type="button" onClick={addTag}>Add</button>
            </div>
          </div>
          <div className="selected-tags">
            {formData.tags.map(tag => (
              <span key={tag} className="tag">
                {tag}
                <button type="button" onClick={() => removeTag(tag)}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="form-section">
          <h3>Images</h3>
          <div className="image-input-section">
            <div className="custom-input">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Add image URL..."
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
              />
              <button type="button" onClick={addImage}>Add Image</button>
            </div>
          </div>
          <div className="image-list">
            {formData.images.map(url => (
              <div key={url} className="image-item">
                <img src={url} alt="Product" className="image-preview" />
                <button type="button" onClick={() => removeImage(url)} className="remove-btn">×</button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <h3>Lab Results (Optional)</h3>
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={labResults.hasResults}
                onChange={(e) => setLabResults(prev => ({ ...prev, hasResults: e.target.checked }))}
              />
              Include Lab Results
            </label>
          </div>
          
          {labResults.hasResults && (
            <div className="lab-results-section">
              <div className="form-row">
                <div className="form-group">
                  <label>7OH Content (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={labResults['7oh']}
                    onChange={(e) => setLabResults(prev => ({ ...prev, '7oh': parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Mitragynine Content (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={labResults.mitragynine}
                    onChange={(e) => setLabResults(prev => ({ ...prev, mitragynine: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Test Date</label>
                  <input
                    type="date"
                    value={labResults.testDate}
                    onChange={(e) => setLabResults(prev => ({ ...prev, testDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Lab Name</label>
                  <input
                    type="text"
                    value={labResults.labName}
                    onChange={(e) => setLabResults(prev => ({ ...prev, labName: e.target.value }))}
                    placeholder="e.g., ACS Laboratory"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Add Product
          </button>
        </div>
      </form>

      <style>{`
        .staff-product-manager {
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
        }

        .product-manager-header {
          margin-bottom: 30px;
          text-align: center;
        }

        .product-manager-header h2 {
          color: #2c5530;
          margin-bottom: 10px;
        }

        .product-form {
          background: #2d3748;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          padding: 30px;
        }

        .form-section {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eee;
        }

        .form-section:last-child {
          border-bottom: none;
        }

        .form-section h3 {
          color: #2c5530;
          margin-bottom: 20px;
          font-size: 1.2em;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #e2e8f0;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #4a5568;
          border-radius: 4px;
          font-size: 14px;
        }

        .form-group textarea {
          resize: vertical;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .checkbox-group {
          display: flex;
          align-items: center;
        }

        .checkbox-group label {
          display: flex;
          align-items: center;
          margin-bottom: 0;
        }

        .checkbox-group input {
          width: auto;
          margin-right: 8px;
        }

        .size-row {
          display: grid;
          grid-template-columns: 1fr 1fr auto auto;
          gap: 15px;
          align-items: end;
          margin-bottom: 10px;
          padding: 15px;
          border: 1px solid #4a5568;
          border-radius: 4px;
        }

        .tag-input-section {
          margin-bottom: 15px;
        }

        .common-options {
          margin-bottom: 15px;
        }

        .button-group {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .tag-btn {
          padding: 6px 12px;
          border: 1px solid #4a5568;
          background: #2d3748;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .tag-btn:hover {
          background: #f5f5f5;
        }

        .tag-btn.selected {
          background: #2c5530;
          color: white;
          border-color: #2c5530;
        }

        .custom-input {
          display: flex;
          gap: 10px;
        }

        .custom-input input {
          flex: 1;
        }

        .custom-input button {
          padding: 10px 20px;
          background: #2c5530;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .selected-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
        }

        .tag {
          display: inline-flex;
          align-items: center;
          background: #e8f5e8;
          color: #2c5530;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
        }

        .tag button {
          background: none;
          border: none;
          color: #2c5530;
          margin-left: 6px;
          cursor: pointer;
          font-weight: bold;
        }

        .image-input-section {
          margin-bottom: 15px;
        }

        .image-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 15px;
          margin-top: 15px;
        }

        .image-item {
          position: relative;
          border: 1px solid #4a5568;
          border-radius: 4px;
          overflow: hidden;
        }

        .image-preview {
          width: 100%;
          height: 100px;
          object-fit: cover;
        }

        .image-item .remove-btn {
          position: absolute;
          top: 5px;
          right: 5px;
          background: rgba(255,255,255,0.9);
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-weight: bold;
        }

        .lab-results-section {
          margin-top: 15px;
          padding: 15px;
          background: #f9f9f9;
          border-radius: 4px;
        }

        .add-btn {
          padding: 8px 16px;
          background: #4CAF50;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .remove-btn {
          padding: 6px 12px;
          background: #f44336;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .form-actions {
          text-align: center;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .submit-btn {
          padding: 15px 30px;
          background: #2c5530;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 600;
        }

        .submit-btn:hover {
          background: #1e3a21;
        }
      `}</style>
    </div>
  )
}